import React, { useMemo } from "react";
import moment from "moment";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  formatPreFinanceMoney,
  formatPreFinancePeriod,
  groupVatSourcesBySupplier,
} from "../utils/preFinancePreviewUtils";

const StackField = ({ label, value }) => (
  <>
    <Grid item xs={5}>
      <Typography>{label}:</Typography>
    </Grid>
    <Grid item xs={5}>{value ?? "—"}</Grid>
  </>
);

const SummaryPanelRow = ({ label, value }) => (
  <Grid container sx={{ mb: 0.25 }}>
    <Grid item xs={8}>
      <Typography sx={{ fontSize: 18, color: "white" }}>{label}:</Typography>
    </Grid>
    <Grid item xs={4}>
      <Typography sx={{ fontSize: 18, color: "white", textAlign: "right" }}>{value}</Typography>
    </Grid>
  </Grid>
);

const VatPreFinanceGroupDetailDialog = ({
  open,
  group,
  countryLabel,
  option,
  isBusy,
  onClose,
  onGroupOptionChange,
  onExcludeSources,
  onRestoreSources,
  excludedSourceIds = [],
}) => {
  const excludedSourceIdSet = useMemo(() => new Set(excludedSourceIds), [excludedSourceIds]);

  const supplierGroups = useMemo(
    () => (group ? groupVatSourcesBySupplier(group.sources) : []),
    [group]
  );

  const activeInvoiceCount = useMemo(() => {
    if (!group) {
      return 0;
    }

    return group.sources.filter((source) => !excludedSourceIdSet.has(source.sourceId)).length;
  }, [group, excludedSourceIdSet]);

  if (!group) {
    return null;
  }

  const lockedClaimLabel = group.lockedQuarterClaim?.claimCode || "—";

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "85vh",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">View pre-finance group</Typography>
          <IconButton aria-label="Close prefinance group details" onClick={onClose} disabled={isBusy}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent
        dividers
        sx={{
          flex: "1 1 0",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
        {!group.isSelectable && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {group.exclusionReason || "This group cannot be selected."}
          </Alert>
        )}
        {group.periodOverrideReason === "LockedQuarterClaim" && (
          <Alert severity="info" sx={{ mb: 2 }}>
            The period is quarterly because claim {group.lockedQuarterClaim?.claimCode} is locked.
          </Alert>
        )}
        {group.missingSourceFileCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {group.missingSourceFileCount} invoice(s) do not have a source file.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <StackField label="Country" value={countryLabel} />
              <StackField label="Period" value={formatPreFinancePeriod(group)} />
              <StackField label="Prefinance Code" value={group.previewPreFinanceCode} />
              <StackField label="Version" value={group.nextVersion ?? "—"} />
              <StackField
                label="VF%"
                value={`${formatPreFinanceMoney(group.appliedVfPercentage)}%`}
              />
              <StackField label="Net Amount" value={formatPreFinanceMoney(group.totalGrossAmount)} />
              <StackField label="VAT EUR" value={formatPreFinanceMoney(group.totalVatAmountEur)} />
              {group.periodOverrideReason === "LockedQuarterClaim" && (
                <StackField label="Locked Claim" value={lockedClaimLabel} />
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6} sx={{ alignSelf: "flex-start" }}>
            <Box sx={{ p: 2, backgroundColor: "#376fd0" }}>
              <SummaryPanelRow label="Invoice Count" value={activeInvoiceCount} />
              <SummaryPanelRow
                label="Total Refund Amount"
                value={formatPreFinanceMoney(group.refundAmount)}
              />
              <SummaryPanelRow
                label="Total Payout Amount"
                value={formatPreFinanceMoney(group.payoutAmount)}
              />
              <SummaryPanelRow label="Total Costs" value={formatPreFinanceMoney(group.totalCosts)} />
              <SummaryPanelRow label="Missing Invoice Copies" value={group.missingSourceFileCount} />
            </Box>
          </Grid>
        </Grid>

        {group.requiresExistingPeriodConfirmation && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" noWrap>
              <strong>A dossier already exists for this exact period:</strong>{" "}
              {group.exactExistingDossiers
                .map(
                  (dossier) =>
                    `${dossier.preFinanceCode} · version ${dossier.version ?? "—"} · payout ${formatPreFinanceMoney(
                      dossier.payoutAmount
                    )}`
                )
                .join("; ")}
            </Typography>
            <FormControlLabel
              sx={{ display: "flex", mt: 1 }}
              control={
                <Checkbox
                  checked={Boolean(option?.continueExistingPeriod)}
                  onChange={(event) =>
                    onGroupOptionChange(
                      group.groupKey,
                      "continueExistingPeriod",
                      event.target.checked
                    )
                  }
                />
              }
              label="I confirm creating an additional pre-finance version for this period"
            />
          </Alert>
        )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            height: 0,
            overflowY: "auto",
            pr: 0.5,
          }}
        >
        <Typography variant="subtitle1" gutterBottom sx={{ flexShrink: 0 }}>
          Linked invoices
        </Typography>

        {supplierGroups.map((supplier) => {
          const activeSources = supplier.sources.filter(
            (source) => !excludedSourceIdSet.has(source.sourceId)
          );
          const isSupplierExcluded = activeSources.length === 0;
          const activeRefundAmount = activeSources.reduce(
            (total, source) => total + Number(source.refundAmount || 0),
            0
          );

          return (
            <Accordion
              key={supplier.supplierId ?? supplier.supplierName}
              defaultExpanded
              disableGutters
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" mr={1}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color={isSupplierExcluded ? "text.disabled" : "text.primary"}
                    >
                      {supplier.supplierName}
                      {isSupplierExcluded ? " (excluded)" : ""}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activeSources.length} of {supplier.sources.length} invoice(s) included · VAT
                      local {formatPreFinanceMoney(activeRefundAmount)}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    color="warning"
                    startIcon={isSupplierExcluded ? <RestoreIcon /> : <RemoveCircleOutlineIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      isSupplierExcluded
                        ? onRestoreSources(supplier.sources.map((source) => source.sourceId))
                        : onExcludeSources(supplier.sources.map((source) => source.sourceId));
                    }}
                    onFocus={(event) => event.stopPropagation()}
                    disabled={isBusy}
                  >
                    {isSupplierExcluded ? "Include supplier" : "Exclude supplier"}
                  </Button>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small" stickyHeader aria-label={`Invoices for ${supplier.supplierName}`}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Net</TableCell>
                        <TableCell align="right">VAT local</TableCell>
                        <TableCell align="right">VAT EUR</TableCell>
                        <TableCell>File</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supplier.sources.map((source) => {
                        const isExcluded = excludedSourceIdSet.has(source.sourceId);

                        return (
                          <TableRow
                            key={source.sourceId}
                            sx={{
                              opacity: isExcluded ? 0.45 : 1,
                              bgcolor: isExcluded ? "action.disabledBackground" : "inherit",
                            }}
                          >
                            <TableCell>{source.sourceCode}</TableCell>
                            <TableCell>
                              {source.sourceDate
                                ? moment(source.sourceDate).format("DD-MM-YYYY")
                                : "—"}
                            </TableCell>
                            <TableCell align="right">
                              {formatPreFinanceMoney(source.grossAmount)}
                            </TableCell>
                            <TableCell align="right">
                              {formatPreFinanceMoney(source.refundAmount)}
                            </TableCell>
                            <TableCell align="right">
                              {formatPreFinanceMoney(source.vatAmountEur)}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={source.fileId ? "Available" : "Missing"}
                                color={source.fileId ? "success" : "warning"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title={isExcluded ? "Include invoice again" : "Exclude invoice"}>
                                <IconButton
                                  aria-label={`${isExcluded ? "Include" : "Exclude"} invoice ${source.sourceCode}`}
                                  color="warning"
                                  size="small"
                                  onClick={() =>
                                    isExcluded
                                      ? onRestoreSources([source.sourceId])
                                      : onExcludeSources([source.sourceId])
                                  }
                                  disabled={isBusy}
                                >
                                  {isExcluded ? (
                                    <RestoreIcon fontSize="small" />
                                  ) : (
                                    <RemoveCircleOutlineIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-start", px: 3, py: 2, flexShrink: 0 }}>
        <Button type="button" variant="contained" color="primary" onClick={onClose} disabled={isBusy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VatPreFinanceGroupDetailDialog;
